import { Alert, Box, Link, useTheme } from '@mui/material';
import { FormattedMessage, useIntl } from 'react-intl';

import { useFeatureFlagSplit } from '@verantos-dev/core-utils';
import { LoadingSpinner } from '@verantos-dev/shared-components';

import { BatchCard } from './BatchCard';
import { useBatchTypeSection } from './useBatchTypeSection';
import { TEST_BATCH_TYPE } from '../../constants';
import type { AccuracyBatchOption } from '../../types/response';
import { getErrorMessage } from '../../utils/extractErrorMessage';
import { SectionShell } from '../SectionShell/SectionShell';
import { TestBatchConfirmDialog } from '../TestBatchConfirmDialog/TestBatchConfirmDialog';

interface BatchTypeSectionProps {
  batches: AccuracyBatchOption[];
  isLoading: boolean;
  isError: boolean;
  error?: unknown;
  selectedBatchId: string | null;
  isComplete: boolean;
  onSelectBatch: (batch: AccuracyBatchOption) => void;
  onConfirmTestBatch: (batch: AccuracyBatchOption) => void;
  onSwitchToValidation: (batch: AccuracyBatchOption) => void;
}

export const BatchTypeSection = ({
  batches,
  isLoading,
  isError,
  error,
  selectedBatchId,
  isComplete,
  onSelectBatch,
  onConfirmTestBatch,
  onSwitchToValidation,
}: BatchTypeSectionProps) => {
  const theme = useTheme();
  const { formatMessage } = useIntl();
  // TODO: cleanup with cp_annotation_show_dashboard_loading_spinner ff
  const showLoadingSpinner = useFeatureFlagSplit(
    'cp_annotation_show_dashboard_loading_spinner'
  ) as boolean;
  const serverErrorMessage = getErrorMessage(error);
  const {
    pendingTestBatch,
    validationBatch,
    handleCardSelect,
    handleDialogCancel,
    handleDialogConfirm,
    handleSwitchToValidation,
  } = useBatchTypeSection({ batches, onSelectBatch, onConfirmTestBatch, onSwitchToValidation });

  const selectedBatch = batches.find((b) => b.batchId === selectedBatchId);
  const isTestBatchSelected = selectedBatch?.batchType === TEST_BATCH_TYPE;

  return (
    <SectionShell
      stepNumber={1}
      title={formatMessage({ id: 'adminProjects.generateAccuracy.batch.title' })}
      description={formatMessage({ id: 'adminProjects.generateAccuracy.batch.description' })}
      isComplete={isComplete}
    >
      {isError ? (
        <Alert severity="error">
          {serverErrorMessage ||
            formatMessage({ id: 'adminProjects.generateAccuracy.batch.loadError' })}
        </Alert>
      ) : showLoadingSpinner && isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 90 }}>
          <LoadingSpinner id="generateAccuracyBatchTypeLoading" />
        </Box>
      ) : batches.length === 0 ? (
        <Alert severity="info">
          {formatMessage({ id: 'adminProjects.generateAccuracy.batch.noEligibleBatches' })}
        </Alert>
      ) : (
        <Box role="radiogroup" sx={{ display: 'flex', gap: theme.spacing(3) }}>
          {batches.map((batch) => (
            <BatchCard
              key={batch.batchId}
              batch={batch}
              selected={batch.batchId === selectedBatchId}
              onSelect={handleCardSelect}
            />
          ))}
        </Box>
      )}

      {isTestBatchSelected && (
        <Alert
          severity="warning"
          sx={{
            mt: theme.spacing(3),
            color: theme.palette.warning.dark,
            borderColor: theme.palette.warning.dark,
          }}
        >
          <FormattedMessage
            id="adminProjects.generateAccuracy.batch.testBatchWarning"
            values={{
              strong: (chunks) => <strong>{chunks}</strong>,
            }}
          />
          {validationBatch?.isSelectableForAccuracy && (
            <>
              {' '}
              <Link
                component="button"
                underline="hover"
                onClick={handleSwitchToValidation}
                sx={{ fontWeight: 500 }}
              >
                {formatMessage({ id: 'adminProjects.generateAccuracy.batch.switchToValidation' })}
              </Link>
            </>
          )}
        </Alert>
      )}

      {pendingTestBatch && (
        <TestBatchConfirmDialog
          open
          encounterCount={pendingTestBatch.visitCount}
          onCancel={handleDialogCancel}
          onConfirm={handleDialogConfirm}
        />
      )}
    </SectionShell>
  );
};
